/**
 * Browser Typesense defaults (aligned with `x-x-x-xppn-new-server` TYPESENSE_*).
 * Override via env: PUBLIC_TYPESENSE_HOST, PUBLIC_TYPESENSE_PORT, PUBLIC_TYPESENSE_PROTOCOL,
 * PUBLIC_TYPESENSE_API_KEY, PUBLIC_TYPESENSE_PRODUCT_COLLECTION
 * Optional: PUBLIC_TYPESENSE_SEARCH_USE_CACHE (default true), PUBLIC_TYPESENSE_SEARCH_CACHE_TTL_SECONDS (default 60)
 * Optional: PUBLIC_TYPESENSE_NL_MODEL_ID — when set with nlQuery, sends nl_query=true and nl_model_id to Typesense.
 */

export const TYPESENSE_BROWSER_DEFAULTS = {
  host: "typesense-docker.azurewebsites.net",
  port: 443,
  protocol: "https" as const,
  productCollection: "products",
  nlModelId: "gemini-model",
  nlQuery: true,
} as const;

function readEnv(key: string): string | undefined {
  return process?.env?.[key]?.trim() || undefined;
}

export function getTypesenseHost(): string {
  return readEnv("PUBLIC_TYPESENSE_HOST") ?? TYPESENSE_BROWSER_DEFAULTS.host;
}

export function getTypesensePort(): number {
  const p = readEnv("PUBLIC_TYPESENSE_PORT");
  const n = p != null && String(p).trim() !== "" ? Number(p) : TYPESENSE_BROWSER_DEFAULTS.port;
  return Number.isFinite(n) ? n : TYPESENSE_BROWSER_DEFAULTS.port;
}

export function getTypesenseProtocol(): "http" | "https" {
  const raw = readEnv("PUBLIC_TYPESENSE_PROTOCOL");
  if (raw === "http") return "http";
  if (raw === "https") return "https";
  return TYPESENSE_BROWSER_DEFAULTS.protocol;
}

export function getTypesenseApiKey(): string | undefined {
  console.log("PUBLIC_TYPESENSE_API_KEY", readEnv("PUBLIC_TYPESENSE_API_KEY"));
  return readEnv("PUBLIC_TYPESENSE_API_KEY");
}

export function getTypesenseProductCollection(): string {
  return TYPESENSE_BROWSER_DEFAULTS.productCollection;
}

/** Typesense server-side search cache; set PUBLIC_TYPESENSE_SEARCH_USE_CACHE=false to disable. */
export function getTypesenseSearchUseCache(): boolean {
  return readEnv("PUBLIC_TYPESENSE_SEARCH_USE_CACHE") !== "false";
}

/** Seconds Typesense keeps identical search responses (HTTP param; scoped keys can also embed TTL). */
export function getTypesenseSearchCacheTtlSeconds(): number {
  const raw = readEnv("PUBLIC_TYPESENSE_SEARCH_CACHE_TTL_SECONDS");
  if (raw === undefined || raw === "") return 60;
  const n = Number(String(raw).trim());
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 60;
}

/** NL search model id (Typesense `nl_search_models`); used with `nl_query=true` on document search. */
export function getTypesenseNlModelId(): string | undefined {
  return TYPESENSE_BROWSER_DEFAULTS.nlModelId;
}

/**
 * Append to your Typesense NL model `system_prompt` (create/update `nl_search_models`).
 * The model often emits invalid float filters like `field:>,5` — Typesense requires `field:>5` (no comma).
 */
export const TYPESENSE_NL_SYSTEM_PROMPT_APPEND = [
  "Typesense filter syntax for numeric (float/int) fields: use `field:>N`, `field:>=N`, `field:<N`, `field:<=N`, or `field:=N` with N a plain number (no spaces, no comma).",
  "Never put a comma between the operator and the number. Invalid: `user_type_price_non_vip_min:>,5`. Valid: `user_type_price_non_vip_min:>5`.",
  "List prices use fields `user_type_price_non_vip_min`, `user_type_price_non_vip_max`, and the gold_vip / diamond_vip equivalents.",
].join(" ");
